import React, { useState } from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'

// actions
import { useHistoricalQuery, GET_FUND_HISTORICAL_HOLDINGS, GET_INST_HISTORICAL_HOLDINGS } from '../../hook'
import { openModal, modalType } from '../../../../actions/ui'
import { useExport } from '../../../../services/csvExport/csvExport.service'

// components
import HistoricalHoldingToolbar from './toolbar/toolbar.component'
import HistoricalHoldingTable from './table/table.component'
import { getPageSizeFromStorage, getQuarterEndDates, ENTITY_TYPE, formatDate } from '../../../../utils'
import { get, isNull, omitBy } from 'lodash'

const { FUND, INSTITUTION } = ENTITY_TYPE

const propTypes = {
  dataIdPrefix: PropTypes.string,
  toolbarTheme: PropTypes.string,
  toolTheme: PropTypes.string,
  entityId: PropTypes.string.isRequired,
  entityType: PropTypes.oneOf([FUND, INSTITUTION]).isRequired
}

const defaultProps = {
  entityType: INSTITUTION
}

const PAGE_SIZE_ID = 'historical-holding-grid'

/**
 * Historical Holding Grid Component
 * @param props
 */
function HistoricalHoldingGrid (props) {
  const { dataIdPrefix, toolbarTheme, toolTheme, entityId, entityType, history, openModal } = props
  const [state, setState] = useState({
    search: null,
    quarters: 4,
    listOptions: {
      page: 1,
      limit: getPageSizeFromStorage(PAGE_SIZE_ID) || 10
    }
  })
  const { search, quarters, listOptions } = state
  const variables = omitBy({
    entityId,
    search,
    quarters,
    ...listOptions
  }, isNull)

  const { data, loading, client } = useHistoricalQuery(entityType, { variables })
  const { generateExport, exporting } = useExport({
    onError: () => {
      openModal({
        type: modalType.ERROR_MODAL
      })
    }
  })

  const source = (entityType === FUND) ? FUND : 'inst'
  const holdings = get(data, `${source}HoldingHistorical.items`, [])
  const total = get(data, `${source}HoldingHistorical.count`, 0)

  /**
   * Handle query change
   * @param query
   */
  const handleQueryChange = (query = {}) => {
    const options = query.listOptions ? { ...query } : { ...query, listOptions: { ...listOptions, page: 1 } }
    setState({ ...state, ...options })
  }

  /**
   * Format mapper for CSV export
   * @param security
   */
  const holdingsCSVMapper = (security) => {
    if (!security) {
      return
    }

    const { securityName } = security
    const fields = (getQuarterEndDates(quarters, moment.utc()) || [])
      .map((date, index) => {
        const formattedDate = formatDate(date, undefined, undefined, true)
        return ({
          [`${formattedDate}-position`]: get(security, `q${(index + 1)}Value`) || '-',
          [`${formattedDate}-change`]: get(security, `q${(index + 1)}Change`) || '-'
        })
      })
      .reduce((obj, item) => ({ ...obj, ...item }), {})

    return { Security: securityName, ...fields }
  }

  /**
   * Handle export
   */
  const handleExport = () => {
    const params = {
      client,
      variables: { ...variables, limit: 0 },
      query: (entityType === FUND) ? GET_FUND_HISTORICAL_HOLDINGS(quarters) : GET_INST_HISTORICAL_HOLDINGS(quarters),
      dataPath: `data.${source}HoldingHistorical.items`,
      fileName: `${source}_historical_holdings.csv`,
      formatter: holdingsCSVMapper
    }

    generateExport(params)
  }

  return (
    <>
      <HistoricalHoldingToolbar
        dataId={`${dataIdPrefix}HistoricalHoldings`}
        toolbarTheme={toolbarTheme}
        toolTheme={toolTheme}
        search={search}
        quarters={quarters}
        noData={!holdings || !holdings.length}
        onQueryChange={handleQueryChange}
        onExport={handleExport}
        exporting={exporting}
      />
      <HistoricalHoldingTable
        pageSizeId={PAGE_SIZE_ID}
        loading={loading}
        data={holdings}
        total={total}
        listOptions={listOptions}
        quarters={quarters}
        onQueryChange={handleQueryChange}
        history={history}
      />
    </>
  )
}

const mapDispatchToProps = (dispatch) => ({
  openModal: bindActionCreators(openModal, dispatch)
})

HistoricalHoldingGrid.propTypes = propTypes
HistoricalHoldingGrid.defaultProps = defaultProps

export default withRouter(connect(null, mapDispatchToProps)(HistoricalHoldingGrid))
